import React, { useEffect, useRef, useState } from "react";import axios from "axios";
import { Bot, Send, X, Sparkles, Loader2, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MiraCopilot — floating "Ask Mira" chat bubble that appears on every
 * dashboard. Backend chooses tools based on the caller's role, so the same
 * widget works for admin / teacher / student / chain admin without any
 * per-role conditional here.
 *
 * Props:
 *   onNavigate?: (moduleId) => void  // called when Mira suggests opening a tab
 *   user:       {name, role, ...}
 */
const suggestionsByRole = {
  super_admin: [
    "Give me a report of students with pending fees",
    "Show low attendance classes today",
    "How many staff do we have and their average salary?",
  ],
  chain_admin: [
    "Compare KPIs across all schools",
    "Which school has the most students?",
  ],
  institute_admin: [
    "Students with pending fees",
    "Low attendance classes today",
    "Open Fee Management",
    "Average staff salary and age",
  ],
  teacher: [
    "Students who scored below 40 in my last test",
    "Create an MCQ paper for Science chapter 4 with 10 medium questions",
    "Open my exam module",
  ],
  student: [
    "What's my homework?",
    "What assessments are pending for me?",
    "Open my digital library",
  ],
  parent: [
    "What homework does my child have?",
    "Pending assessments for my child?",
  ],
};

const MiraCopilot = ({ user, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem("mira_session_id") || ""
  );
  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(
    () => localStorage.getItem("mira_tts_enabled") === "1"
  );
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("mira_tts_enabled", ttsEnabled ? "1" : "0");
    if (!ttsEnabled && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  }, [ttsEnabled]);

  useEffect(() => {
    if (sessionId) localStorage.setItem("mira_session_id", sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
    // Steal focus when the panel opens so keystrokes don't bleed into the
    // underlying page's search / form controls (spotted by testing agent).
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [messages, open]);

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await axios.post(
        `${API}/mira/chat`,
        { session_id: sessionId || undefined, message: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = resp.data || {};
      if (data.session_id) setSessionId(data.session_id);
      const replyText = data.reply || "…";
      setMessages((m) => [...m, { role: "assistant", text: replyText }]);
      // Optional voice playback of Mira's reply
      if (ttsEnabled && "speechSynthesis" in window && replyText) {
        try {
          window.speechSynthesis.cancel();
          // Strip markdown asterisks + emojis for cleaner TTS
          const clean = replyText.replace(/[*_#`]|[\u{1F300}-\u{1FAFF}]/gu, "");
          const u = new SpeechSynthesisUtterance(clean);
          u.rate = 1.05;
          u.pitch = 1.0;
          window.speechSynthesis.speak(u);
        } catch { /* ignore TTS errors */ }
      }
      // Fire navigation actions if Mira asked for them
      (data.actions || []).forEach((a) => {
        if (a.type === "navigate" && a.module_id && typeof onNavigate === "function") {
          onNavigate(a.module_id);
        }
      });
    } catch (e) {
      const msg = e.response?.data?.detail || "Mira is unavailable right now.";
      setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${msg}` }]);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const role = user?.role || "student";
  const suggestions = suggestionsByRole[role] || suggestionsByRole.student;

  // ----- Voice: mic recording → Whisper transcription -----
  const startRecording = async () => {
    if (isRecording || transcribing) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 800) {
          toast.info("Too short — hold to speak, release to send");
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("language", "en");
          const token = localStorage.getItem("token");
          const r = await axios.post(`${API}/mira/transcribe`, fd, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const text = (r.data?.text || "").trim();
          if (!text) {
            toast.info("Didn't catch that — try again");
          } else {
            await send(text);
          }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Transcription failed");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      setIsRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    try { mediaRef.current?.stop(); } catch { /* noop */ }
    setIsRecording(false);
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="mira-copilot-toggle"
        className="fixed bottom-20 right-6 z-40 flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-3 shadow-2xl hover:shadow-purple-500/50 hover:scale-105 transition-all"
        aria-label="Ask Mira"
      >
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-semibold">Ask Mira</span>
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="mira-copilot-panel"
          className="fixed bottom-36 right-6 z-40 w-[380px] max-w-[92vw] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-semibold leading-tight">Mira Copilot</div>
                <div className="text-[10px] opacity-80 leading-tight">
                  {role.replace(/_/g, " ")} · powered by Claude
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setTtsEnabled((v) => !v)}
                className="p-1.5 rounded hover:bg-white/20"
                data-testid="mira-tts-toggle"
                aria-label={ttsEnabled ? "Mute Mira" : "Enable voice reply"}
                title={ttsEnabled ? "Voice reply: ON" : "Voice reply: OFF"}
              >
                {ttsEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 opacity-70" />}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/20"
                data-testid="mira-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="text-sm text-slate-600">
                  Hi {user?.name?.split(" ")[0] || "there"}! I can pull data, open modules, and even draft
                  question papers. Try one of these:
                </div>
                <div className="space-y-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="w-full text-left text-xs bg-white border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 rounded-lg px-3 py-2 transition-colors"
                      data-testid={`mira-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-indigo-600 text-white rounded-br-sm"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                  }`}
                  data-testid={`mira-msg-${m.role}-${i}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Mira is thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-slate-200 p-3 flex items-center gap-2 bg-white"
          >
            <button
              type="button"
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onMouseLeave={isRecording ? stopRecording : undefined}
              onTouchStart={startRecording}
              onTouchEnd={stopRecording}
              disabled={sending || transcribing}
              title="Hold to speak"
              className={`h-10 w-10 flex items-center justify-center rounded-lg transition-colors ${
                isRecording
                  ? "bg-red-500 text-white animate-pulse"
                  : transcribing
                  ? "bg-slate-200 text-slate-500"
                  : "bg-slate-100 text-slate-600 hover:bg-indigo-100 hover:text-indigo-600"
              }`}
              data-testid="mira-mic-btn"
            >
              {transcribing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isRecording ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </button>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isRecording ? "Listening…" : transcribing ? "Transcribing…" : "Ask anything…"}
              className="flex-1 h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:outline-none text-sm"
              data-testid="mira-input"
              disabled={isRecording || transcribing}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="h-10 px-3 rounded-lg bg-indigo-600 text-white disabled:opacity-50 hover:bg-indigo-700"
              data-testid="mira-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default MiraCopilot;
